import { useState, useCallback } from "react";
import NavBar from "@/components/NavBar";
import SEOHead from "@/components/SEOHead";
import BreadcrumbSEO from "@/components/BreadcrumbSEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Upload, Download, Trash2, Loader2, ImageIcon } from "lucide-react";
import { removeBackgroundFromFile } from "@/utils/backgroundRemoval";

const RemoveBackground = () => {
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);
  const [processedUrl, setProcessedUrl] = useState<string | null>(null);
  const [processedFile, setProcessedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState<string>("");

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.type.startsWith("image/")) {
      toast({
        title: "Archivo no válido",
        description: "Por favor selecciona una imagen (JPG, PNG, WebP)",
        variant: "destructive",
      });
      return;
    }

    // Clean up previous URLs
    if (originalUrl) URL.revokeObjectURL(originalUrl);
    if (processedUrl) URL.revokeObjectURL(processedUrl);

    setFile(selectedFile);
    setOriginalUrl(URL.createObjectURL(selectedFile));
    setProcessedUrl(null);
    setProcessedFile(null);
    setProgress("");
  }, [originalUrl, processedUrl, toast]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (!droppedFile) return;

    if (!droppedFile.type.startsWith("image/")) {
      toast({
        title: "Archivo no válido",
        description: "Por favor selecciona una imagen (JPG, PNG, WebP)",
        variant: "destructive",
      });
      return;
    }

    // Clean up previous URLs
    if (originalUrl) URL.revokeObjectURL(originalUrl);
    if (processedUrl) URL.revokeObjectURL(processedUrl);

    setFile(droppedFile);
    setOriginalUrl(URL.createObjectURL(droppedFile));
    setProcessedUrl(null);
    setProcessedFile(null);
    setProgress("");
  }, [originalUrl, processedUrl, toast]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleRemoveBackground = async () => {
    if (!file) return;

    setIsProcessing(true);
    setProgress("Iniciando...");

    try {
      const result = await removeBackgroundFromFile(file, setProgress);
      setProcessedUrl(result.processedUrl);
      setProcessedFile(result.processedFile);
      setProgress("");
      toast({
        title: "¡Fondo eliminado!",
        description: "Tu imagen está lista para descargar",
      });
    } catch (error) {
      console.error("Error removing background:", error);
      toast({
        title: "Error al eliminar fondo",
        description: "Ocurrió un error al procesar la imagen. Intenta de nuevo.",
        variant: "destructive",
      });
      setProgress("");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!processedFile || !processedUrl) return;

    const link = document.createElement("a");
    link.href = processedUrl;
    link.download = processedFile.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClear = () => {
    if (originalUrl) URL.revokeObjectURL(originalUrl);
    if (processedUrl) URL.revokeObjectURL(processedUrl);
    setFile(null);
    setOriginalUrl(null);
    setProcessedUrl(null);
    setProcessedFile(null);
    setProgress("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      <SEOHead
        title="Quitar Fondo de Imagen Online Gratis | CompresorJPEG"
        description="Elimina el fondo de tus imágenes automáticamente con IA. Herramienta gratuita para quitar fondos de fotos, logos y productos. Sin instalación."
        keywords="quitar fondo imagen, eliminar fondo foto, background remover, remover fondo online, transparente png"
        canonical="https://compresorjpeg.com/quitar-fondo"
      />

      <NavBar />

      <div className="container mx-auto px-4">
        <BreadcrumbSEO />
      </div>

      <header className="text-center py-12 animate-fade-in">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            Quitar Fondo de Imagen Online
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Elimina el fondo de tus imágenes automáticamente con <strong>inteligencia artificial</strong>. 
            Ideal para logos, productos y fotos de perfil.
          </p>
        </div>
      </header>

      <main className="container mx-auto px-4 pb-16">
        <Card className="max-w-4xl mx-auto p-6 bg-white/80 backdrop-blur-sm">
          {!file ? (
            <div
              className="border-2 border-dashed border-purple-300 rounded-xl p-12 text-center cursor-pointer hover:border-purple-500 hover:bg-purple-50/50 transition-all"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => document.getElementById("file-input")?.click()}
            >
              <input
                id="file-input"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Upload className="h-16 w-16 mx-auto text-purple-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                Arrastra tu imagen aquí
              </h3>
              <p className="text-gray-500">
                o haz clic para seleccionar un archivo
              </p>
              <p className="text-sm text-gray-400 mt-2">
                Soporta JPG, PNG, WebP
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Image comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Original */}
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-700 text-center">Original</h3>
                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                    {originalUrl ? (
                      <img
                        src={originalUrl}
                        alt="Imagen original"
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <ImageIcon className="h-12 w-12 text-gray-300" />
                    )}
                  </div>
                </div>

                {/* Processed */}
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-700 text-center">Sin Fondo</h3>
                  <div 
                    className="aspect-square rounded-lg overflow-hidden flex items-center justify-center"
                    style={{
                      backgroundImage: "linear-gradient(45deg, #e0e0e0 25%, transparent 25%), linear-gradient(-45deg, #e0e0e0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e0e0e0 75%), linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)",
                      backgroundSize: "20px 20px",
                      backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
                      backgroundColor: "#f5f5f5"
                    }}
                  >
                    {isProcessing ? (
                      <div className="text-center">
                        <Loader2 className="h-12 w-12 text-purple-500 animate-spin mx-auto mb-2" />
                        <p className="text-sm text-gray-600">{progress}</p>
                      </div>
                    ) : processedUrl ? (
                      <img
                        src={processedUrl}
                        alt="Imagen sin fondo"
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <div className="text-center text-gray-400">
                        <ImageIcon className="h-12 w-12 mx-auto mb-2" />
                        <p className="text-sm">Presiona "Quitar Fondo"</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* File info */}
              <div className="text-center text-sm text-gray-500">
                <p>{file.name} • {(file.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap justify-center gap-4">
                {!processedUrl ? (
                  <Button
                    onClick={handleRemoveBackground}
                    disabled={isProcessing}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Procesando...
                      </>
                    ) : (
                      "Quitar Fondo"
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handleDownload}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Descargar PNG
                  </Button>
                )}

                <Button
                  variant="outline"
                  onClick={handleClear}
                  disabled={isProcessing}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Nueva imagen
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Features section */}
        <section className="max-w-4xl mx-auto mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🤖</span>
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">IA Avanzada</h3>
            <p className="text-gray-600 text-sm">
              Usa modelos de inteligencia artificial para detectar y eliminar fondos con precisión.
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">⚡</span>
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">100% En el Navegador</h3>
            <p className="text-gray-600 text-sm">
              Tus imágenes no se suben a ningún servidor. Todo el procesamiento ocurre en tu dispositivo.
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🎨</span>
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">PNG Transparente</h3>
            <p className="text-gray-600 text-sm">
              Descarga tu imagen en formato PNG con fondo transparente, lista para usar.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default RemoveBackground;
